import { computed, ref } from 'vue';

import type { LegalDoc } from './legal-slugs';
import { useSession } from './session';
import { fetchLegalList } from '../landing/site-api';

import {
  ApiError,
  api,
  type AccountPreferences,
  type AccountProfile,
  type BillingAddress,
  type I18nConfig,
  type InvoiceRow,
  type LegalDocument,
  type PurchaseRow,
} from '@/net/api';
import { showNotice } from './notification-banner';

/**
 * The profile screen's state.
 *
 * A module-scoped store like `state/garage.ts` and for the same reason: there is
 * one account per tab, and the overlay is opened and closed repeatedly, so
 * holding this in the component would refetch four lists every time somebody
 * looked at their own email address.
 *
 * **Every pane loads on demand and only once.** The overlay opens on Account, and
 * a player who never presses Billing never fetches an invoice — which matters
 * because the PDF list, the ledger and the legal documents (the list itself,
 * plus one request per document) are requests nobody has asked for. `loaded`
 * is per pane rather than one flag, so
 * "not fetched yet" and "fetched and genuinely empty" stay different states; an
 * empty Billing pane says *no transactions yet* rather than spinning for ever.
 *
 * **Nothing here derives a figure.** Prices, totals, tax and the rank curve are
 * all the server's; this store holds responses and the one-line status of the
 * request that fetched them. The one thing it computes is `verified`, which is a
 * field read.
 */

const profile = ref<AccountProfile | null>(null);
const preferences = ref<AccountPreferences | null>(null);
const config = ref<I18nConfig | null>(null);

const invoices = ref<InvoiceRow[]>([]);
const invoiceTotal = ref(0);
const invoicePage = ref(1);

const purchases = ref<PurchaseRow[]>([]);
const purchaseTotal = ref(0);
const purchasePage = ref(1);

const legal = ref<Record<string, LegalDocument | null>>({});
/** The dynamic document list itself — see `loadLegal()` below. */
const legalDocs = ref<LegalDoc[]>([]);

const loaded = ref({ account: false, billing: false, purchases: false, legal: false });
const busy = ref(false);
const error = ref<string | null>(null);

export function useAccount() {
  return {
    profile: computed(() => profile.value),
    preferences: computed(() => preferences.value),
    config: computed(() => config.value),

    invoices: computed(() => invoices.value),
    invoiceTotal: computed(() => invoiceTotal.value),
    invoicePage: computed(() => invoicePage.value),

    purchases: computed(() => purchases.value),
    purchaseTotal: computed(() => purchaseTotal.value),
    purchasePage: computed(() => purchasePage.value),

    legal: computed(() => legal.value),
    legalDocs: computed(() => legalDocs.value),

    loaded: computed(() => loaded.value),
    busy: computed(() => busy.value),
    error: computed(() => error.value),

    /** Whether the address is confirmed, as the account itself reports it. */
    verified: computed(() => profile.value?.emailVerified === true),

    loadAccount,
    loadBilling,
    loadPurchases,
    loadLegal,
    saveFullName,
    saveCallSign,
    saveBillingAddress,
    savePreferences,
    changePassword,
    resendVerification,
    deleteAccount,
    clear,
    dismiss,
  };
}

/**
 * Account pane. `force` re-reads after something changed it.
 *
 * The preferences and the option lists are fetched alongside, because all three
 * are the same pane and three sequential round trips would show it in three
 * states. `Promise.all` rather than awaiting in turn, for the same reason.
 */
async function loadAccount(force = false): Promise<void> {
  if (loaded.value.account && !force) return;
  await run(async () => {
    const [next, prefs, cfg] = await Promise.all([
      api.account(),
      api.preferences(),
      // Cached after the first open: the enabled lists change when the server is
      // redeployed, not while somebody has a dialog open.
      config.value ? Promise.resolve(config.value) : api.i18nConfig(),
    ]);
    profile.value = next;
    preferences.value = prefs;
    config.value = cfg;
    loaded.value.account = true;
  });
}

async function loadBilling(page = 1): Promise<void> {
  if (loaded.value.billing && page === invoicePage.value) return;
  await run(async () => {
    const result = await api.invoices(page);
    invoices.value = result.items;
    invoiceTotal.value = result.total;
    invoicePage.value = result.page;
    loaded.value.billing = true;
  });
}

async function loadPurchases(page = 1): Promise<void> {
  if (loaded.value.purchases && page === purchasePage.value) return;
  await run(async () => {
    const result = await api.purchases(page);
    purchases.value = result.items;
    purchaseTotal.value = result.total;
    purchasePage.value = result.page;
    loaded.value.purchases = true;
  });
}

/**
 * The legal documents — the dynamic list first, then each one's content.
 *
 * The list itself comes from `landing/site-api.ts`'s `fetchLegalList()` (off
 * the public `GET /api/legal`), the same call the landing page's footer makes
 * — so the profile screen and a stranger's footer are reading the identical,
 * live, published set rather than two hardcoded copies of "the three
 * documents" that could drift apart. See `state/legal-slugs.ts` for what used
 * to live there instead.
 *
 * Content fetches stay **individually tolerant**: one unpublished document
 * must not blank the others, so each rejection resolves to `null` rather than
 * failing the batch — the same rule `landing/site-api.ts` applies for the
 * footer.
 */
async function loadLegal(): Promise<void> {
  if (loaded.value.legal) return;
  await run(async () => {
    const docs = await fetchLegalList();
    legalDocs.value = docs;
    const pages = await Promise.all(
      docs.map(({ slug }) => api.legalDocument(slug).catch(() => null)),
    );
    const next: Record<string, LegalDocument | null> = {};
    docs.forEach(({ slug }, i) => {
      next[slug] = pages[i] ?? null;
    });
    legal.value = next;
    loaded.value.legal = true;
  });
}

async function saveFullName(fullName: string): Promise<boolean> {
  return run(async () => {
    profile.value = await api.updateAccount(fullName);
    showNotice({ title: 'Saved.', lines: [] });
  });
}

/**
 * Changes the call sign.
 *
 * The confirmation names the new call sign rather than saying "Saved.", which
 * the name field above can get away with and this cannot: this is the string
 * other players see, the server may have trimmed it, and reading it back is the
 * only proof the change is the one that was asked for.
 *
 * `run` turns a refusal into `false` and puts the server's sentence in `error`,
 * where the overlay's own `.status.error` shows it — so a taken name is reported
 * in the same place and the same shape as every other failure on this screen.
 */
async function saveBillingAddress(address: BillingAddress): Promise<boolean> {
  return run(async () => {
    profile.value = await api.updateBillingAddress(address);
    showNotice({ title: 'Address saved.', lines: [] });
  });
}

async function saveCallSign(callSign: string): Promise<boolean> {
  return run(async () => {
    profile.value = await api.updateCallSign(callSign);
    const nickname = profile.value.callSign ?? callSign;
    // `state/session.ts` holds a second copy of the player's own name — the
    // one the header, the battle join payload and the in-battle roster all
    // read — and it does not see this response. Without this it stayed
    // stale until the access token's next silent refresh.
    useSession().setNickname(nickname);
    showNotice({ title: `Call sign is now ${nickname}.`, lines: [] });
  });
}

/**
 * Writes language, currency or both.
 *
 * The server's answer replaces the local copy rather than the local copy being
 * patched — a currency it refuses (dropped from `CURRENCIES` since the dropdown
 * was drawn) must leave the screen showing what is actually stored.
 */
async function savePreferences(next: { locale?: string; currency?: string }): Promise<boolean> {
  return run(async () => {
    preferences.value = await api.updatePreferences(next);
    showNotice({ title: 'Saved.', lines: [] });
  });
}

/**
 * Changes the password. The caller signs out on success.
 *
 * Every session is revoked server-side and both cookies are cleared, so there is
 * nothing left to stay signed in with — see `AccountService.changePassword`. The
 * store does not sign out itself, because that would mean importing the session
 * store into this one; the pane that owns the form does it.
 */
async function changePassword(currentPassword: string, newPassword: string): Promise<boolean> {
  return run(async () => {
    await api.changePassword(currentPassword, newPassword);
  });
}

/**
 * Asks for another verification email.
 *
 * Returns the dev link when the server offered one — a stack with no SMTP would
 * otherwise leave the address unconfirmable, and with nothing in the game
 * reachable unverified that is a dead end rather than an inconvenience. See
 * `AccountService.resendVerification`.
 */
async function resendVerification(): Promise<string | null> {
  let link: string | null = null;
  await run(async () => {
    const result = await api.resendVerification();
    link = result.devVerifyUrl ?? null;
    showNotice({ title: 'Sent. Check your inbox.', lines: [] });
  });
  return link;
}

/** Deletes the account. The caller signs out — there is nothing to sign into. */
async function deleteAccount(password: string, confirmEmail: string): Promise<boolean> {
  return run(async () => {
    await api.deleteAccount(password, confirmEmail);
  });
}

/**
 * Forgets everything, on sign-out.
 *
 * Not merely tidiness: this store outlives the component *and* the session, so
 * without it the next account to sign in on the same tab would open the profile
 * screen on the previous one's email address and invoices for the moment before
 * the first fetch landed.
 */
function clear(): void {
  profile.value = null;
  preferences.value = null;
  invoices.value = [];
  invoiceTotal.value = 0;
  invoicePage.value = 1;
  purchases.value = [];
  purchaseTotal.value = 0;
  purchasePage.value = 1;
  legal.value = {};
  legalDocs.value = [];
  loaded.value = { account: false, billing: false, purchases: false, legal: false };
  error.value = null;
  // `config` deliberately survives: the enabled locales and currencies are a
  // property of the server, not of whoever is signed in.
}

function dismiss(): void {
  error.value = null;
}

/** One place for the busy flag and the error wording, as the garage store does. */
async function run(action: () => Promise<void>): Promise<boolean> {
  busy.value = true;
  error.value = null;
  try {
    await action();
    return true;
  } catch (err) {
    error.value =
      err instanceof ApiError ? err.message : 'Could not reach the server. Try again.';
    return false;
  } finally {
    busy.value = false;
  }
}
